import * as React from "react";
import { PaginationNavigation, AlertMessage, AlertType, CustomButton, ButtonType } from "soprasteria-ui-library";
import { useAuth } from "../../../App";
import { ImportXmlApi } from "../../../api/ImportXmlApi";
import { IImportedXmlSummary, IImportedXmlSearchParams } from "../../../models/IImportedXml";
import { IPagination } from "soprasteria-ui-library/dist/models/IPagination";
import { setMessage } from "../../../helpers/MessageHelper";
import { UploadXmlModal } from "./UploadXmlModal";
import { ImportedXmlsTable } from "./ImportedXmlsTable";
import { IListItem } from "soprasteria-ui-library/dist/models/IFormInput";
import { ImportedXmlSearchForm } from "./ImportedXmlSearchForm";
import { isInRole, UserContext } from "../../../contexts/UserContext";
import { LIBRETTOADMIN, VÅRDANSVARIG } from "../../../utils/RoleConstants";
import { useHistory } from "react-router";
import { IMPORTED_XMLS } from "../../../utils/RouteConstants";

export const ImportedXmls: React.FunctionComponent<{}> = () => {

    const auth = useAuth();
    const api = new ImportXmlApi(auth);
    const abortController = new AbortController();
    const userInfo = React.useContext(UserContext);
    const hasRoleSysAdmin = isInRole(LIBRETTOADMIN, userInfo.roles);
    const hasRoleParaply = isInRole(VÅRDANSVARIG, userInfo.roles);

    const [isFetchingXmls, setIsFetchingXmls] = React.useState<boolean>(false);
    const [isFetchingXmlTypes, setIsFetchingXmlTypes] = React.useState<boolean>(false);
    const [renderUploadModal, setRenderUploadModal] = React.useState<boolean>(false);

    const [errorMessage, setErrorMessage] = React.useState<string>("");
    const [successMessage, setSuccessMessage] = React.useState<string>("");

    const [importedXmls, setImportedXmls] = React.useState<IImportedXmlSummary[]>([]);
    const [xmlTypes, setXmlTypes] = React.useState<IListItem[]>([]);
    
    const pageSize = 20;
    const [searchParams, setSearchParams] = React.useState<IImportedXmlSearchParams>({ pageNumber: 1, pageSize: pageSize });
    const [paginationHeader, setPaginationHeader] = React.useState<IPagination>();
    const history = useHistory();

    React.useEffect(() => {
        getXmlTypes();
        if (history.location.state !== undefined && history.location.state !== null) {
            const lastSearchParams = history.location.state as IImportedXmlSearchParams;
            searchImportedXmls(lastSearchParams);
        }
        else {
            searchImportedXmls({ pageSize: pageSize, pageNumber: 1 });
        }

        return () => {
            abortController.abort();
        }
    }, []);

    const searchImportedXmls = (data: IImportedXmlSearchParams) => {
        setIsFetchingXmls(true);

        data.pageSize = pageSize;
        data.pageNumber = data.pageNumber ?? 1;
        history.push(IMPORTED_XMLS, data);

        api.getImportedXmls(data, (response) => {
            setImportedXmls(response.data);
            setPaginationHeader(response.paginationHeader);
        }, (error) => {
            changeErrorMessage(error);
        }, () => {
            setIsFetchingXmls(false)
        });

        setSearchParams(data);
    }

    const getXmlTypes = () => {
        setIsFetchingXmlTypes(true);
        api.getXmlTypes((types) => {
            const newTypes = types.map<IListItem>((type) => {
                return { text: type.description, value: type.importedXmlTypeId.toString() }
            });
            setXmlTypes(newTypes)
        }, (error) => {
            changeErrorMessage(error);
        }, () => {
            setIsFetchingXmlTypes(false);
        });
    }

    const onUploadFormSuccess = (message: string) => {
        changeSuccessMessage(message);
        searchImportedXmls(history.location.state !== undefined && history.location.state !== null ? history.location.state as any : { pageNumber: 1, pageSize: 20 });
    }

    const changeSuccessMessage = (message: string) => {
        const newMessage = setMessage(message, successMessage)
        setSuccessMessage(newMessage);
    }

    const changeErrorMessage = (message: string) => {
        const newMessage = setMessage(message, errorMessage)
        setErrorMessage(newMessage);
    }

    const onPageChange = (pageNumber: number) => {
        if (searchParams) {
            setIsFetchingXmls(true);
            var currentSearchParams = searchParams;
            currentSearchParams.pageNumber = pageNumber;
            history.push(IMPORTED_XMLS, currentSearchParams);

            api.getImportedXmls(currentSearchParams, (response) => {
                setImportedXmls(response.data);
                setPaginationHeader(response.paginationHeader);
            }, undefined, () => setIsFetchingXmls(false));
        }
    }

    return (
        <section className="card">
            <div className="card-header">
                <h1>Sök importfiler</h1>
            </div>
            <div className="card-body">
                <ImportedXmlSearchForm importedXmlTypes={xmlTypes} searchImportedXmls={searchImportedXmls} isFetchingImportedXmls={isFetchingXmls} preSetSearchParams={history.location.state !== undefined && history.location.state !== null ? history.location.state as any : undefined} />
                <div className="text-right">
                    <CustomButton buttonType={ButtonType.create} data-toggle="modal" data-target="#upload-xml-modal" buttonText="Ladda upp fil" disabled={hasRoleSysAdmin} onClick={() => setRenderUploadModal(true)} />
                </div>
                <AlertMessage alertType={AlertType.danger} duration={3000}>{errorMessage}</AlertMessage>
                <AlertMessage alertType={AlertType.success} duration={3000}>{successMessage}</AlertMessage>
                <ImportedXmlsTable data={importedXmls} isFetchingXmls={isFetchingXmls} selectedCareProviderId={searchParams.careProviderId} canViewImportXmlAsCareProvider={hasRoleSysAdmin || hasRoleParaply} />
                <PaginationNavigation
                    ariaLabel="xml pagination selector"
                    pagination={paginationHeader}
                    pageNeighbours={2}
                    showCount={true}
                    selectedPage={onPageChange} />
            </div>
            <UploadXmlModal xmlTypes={xmlTypes} onSuccess={onUploadFormSuccess} renderForm={renderUploadModal} onClose={() => setRenderUploadModal(false)} isFetchingXmlTypes={isFetchingXmlTypes} />
        </section>
    );
}